import { useState, useEffect, useContext } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import { Spinner } from 't-lib-ui/widgets';
import { AppState } from '../context/general';
import { getPublicFeed } from '../connector/api';
import { markAnonFromShare, applyFirstFeedDefaults } from '../connector/anon-onboarding';
import { RestCatalog, RestUserFeed } from '../schema-copy/api-schema';
import ShareHeader from '../components/ShareHeader';
import LegalFooter from '../components/LegalFooter';
import FeedTile from '../components/FeedTile';
import FeedPreview from '../components/FeedPreview';
import './SharePage.css';

const ShareFeedPage: React.FC = () => {
  const { f: feedId } = useParams<{ f: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  const auth = appState.state.authData;
  const isLoggedIn = appState.state.loginState == 'ok';
  const [feed, setFeed] = useState<RestUserFeed | RestCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [trying, setTrying] = useState(false);

  useEffect(() => {
    if (!feedId) return;
    let cancelled = false;

    const loadFeed = async () => {
      setLoading(true);
      setError(null);
      try {
        const feedData = await getPublicFeed(feedId, auth);
        if (!cancelled) setFeed(feedData);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFeed();
    return () => { cancelled = true; };
  }, [feedId, auth]);

  // Following works the same for logged-in and anonymous readers (the anon engine keeps the
  // subscription in localStorage), so reflect the live subscribed state from whichever engine is active.
  const isSubscribed = () => !!feed && feedEngine.getFeeds().some(f => f.id == feed.id);
  const [subscribed, setSubscribed] = useState(false);
  useEffect(() => {
    setSubscribed(isSubscribed());
    return feedEngine.onFeedsChange(() => setSubscribed(isSubscribed()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, feedEngine]);

  // "Try now": no account needed — follow this feed in the active (anon) engine and drop straight
  // into the reader so the visitor starts reading immediately.
  const handleTryNow = async () => {
    if (!feed || trying) return;
    setTrying(true);
    try {
      feedEngine.registerFeedInfo(feed);
      await feedEngine.subscribe(feed.id);
      applyFirstFeedDefaults(feedEngine, feed.id);
      markAnonFromShare();
      goTo(routerCtx, `/a/feed/${feed.id}`);
    } catch (e) {
      console.error('Try now failed:', e);
      setTrying(false);
    }
  };

  // Follow from the tile: once the subscribe lands, drop the reader straight into the feed so they can
  // start reading. Only navigate on follow (now subscribed), not when the action was an unfollow.
  const handleTileAction = () => {
    if (feed && feedEngine.getFeeds().some(f => f.id == feed.id)) {
      applyFirstFeedDefaults(feedEngine, feed.id);
      markAnonFromShare();
      goTo(routerCtx, `/a/feed/${feed.id}`);
    }
  };

  // CTA shown to logged-out visitors. Logged-in users already have the inline Follow/View controls
  // on the tile, so they don't see this.
  const visitorCtas = (
    <div className="shareBannerActions">
      <button type="button" className="shareBannerCta" onClick={handleTryNow} disabled={trying}>
        {trying ? 'Opening…' : 'Try now'}
      </button>
    </div>
  );

  return (
    <div className="sharePage">
      <ShareHeader />
      <div className="sharePageContent">
        {loading && (
          <div className="sharePageLoading">
            <Spinner spinning />
            <span>Loading feed...</span>
          </div>
        )}

        {error && (
          <div className="sharePageError">
            <h2>Feed not found</h2>
            <p>{error}</p>
            <a href="https://downread.com" className="shareBannerCta">Discover feeds on Downread</a>
          </div>
        )}

        {feed && !loading && (
          <>
            {!isLoggedIn && (
              <div className="shareBanner">
                <div className="shareBannerText">
                  Read <strong>{feed.shortTitle || feed.title}</strong> on{' '}
                  <a href="https://downread.com">Downread</a>, a free RSS reader.{' '}
                  <strong>No account needed</strong>. Start reading right away.
                </div>
                {visitorCtas}
              </div>
            )}

            <div className="shareFeedTileWrapper">
              <FeedTile
                feed={feed}
                subscribed={subscribed}
                showPrivacyNote
                onAction={handleTileAction}
              />
            </div>

            <FeedPreview feed={feed} maxPosts={20} linkToSharePosts />

            {!isLoggedIn && (
              <div className="shareBanner bottom">
                <div className="shareBannerText">
                  Read <strong>{feed.shortTitle || feed.title}</strong> and thousands of other feeds on{' '}
                  <a href="https://downread.com">Downread</a>. No sign-up required to get started.
                </div>
                {visitorCtas}
              </div>
            )}
          </>
        )}
        <LegalFooter bottom />
      </div>
    </div>
  );
};

export default ShareFeedPage;
